(() => {
  'use strict';

  const PLUS_SELECTOR = 'body.template-product #main-product .l4pr a.swiper-pagination-bullet.has-more';
  let modal = null;
  let stageImage = null;
  let counter = null;
  let thumbs = null;
  let galleryItems = [];
  let activeIndex = 0;
  let lastFocus = null;
  let touchStartX = 0;

  const largestFromSrcset = (img) => {
    const srcset = img.getAttribute('srcset') || '';
    if (!srcset) return '';
    const candidates = srcset.split(',').map((part) => {
      const bits = part.trim().split(/\s+/);
      const src = bits[0] || '';
      const size = Number.parseInt((bits[1] || '').replace(/\D/g, ''), 10) || 0;
      return { src, size };
    }).filter((item) => item.src);
    candidates.sort((a, b) => b.size - a.size);
    return candidates[0]?.src || '';
  };

  const isImageHref = (href) => {
    if (!href) return false;
    return /cdn\.shopify\.com|cdn\.shopifycdn\.net|\.(?:jpe?g|png|webp|avif)(?:[?#]|$)/i.test(href) && !/\/products\//i.test(href);
  };

  const upgradeShopifyImage = (src) => {
    if (!src) return '';
    try {
      const url = new URL(src, location.origin);
      if (/shopify|shopifycdn/i.test(url.hostname) && url.searchParams.has('width')) {
        url.searchParams.set('width', '1800');
      }
      return url.href;
    } catch (error) {
      return src;
    }
  };

  const imageSource = (img) => {
    const anchorHref = img.closest('a[href]')?.href || '';
    const src = isImageHref(anchorHref)
      ? anchorHref
      : (largestFromSrcset(img) || img.currentSrc || img.src || img.dataset.src || '');
    return upgradeShopifyImage(src);
  };

  const collectGallery = (gallery) => {
    const seen = new Set();
    const items = [];
    gallery.querySelectorAll('.swiper-slide img').forEach((img) => {
      const src = imageSource(img);
      if (!src) return;
      let key = src;
      try {
        const url = new URL(src, location.origin);
        url.searchParams.delete('width');
        key = url.pathname + '?' + url.searchParams.toString();
      } catch (error) {}
      if (seen.has(key)) return;
      seen.add(key);
      items.push({
        src,
        alt: img.getAttribute('alt') || document.querySelector('#main-product h1')?.textContent?.trim() || 'TOVYNX product image'
      });
    });
    return items;
  };

  const buildModal = () => {
    if (modal) return modal;

    modal = document.createElement('div');
    modal.className = 'tvx-full-gallery';
    modal.hidden = true;
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-label', 'Product image gallery');

    const top = document.createElement('div');
    top.className = 'tvx-full-gallery__top';

    counter = document.createElement('div');
    counter.className = 'tvx-full-gallery__counter';
    counter.setAttribute('aria-live', 'polite');

    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'tvx-full-gallery__close';
    close.setAttribute('aria-label', 'Close product gallery');
    close.textContent = '×';
    close.addEventListener('click', closeGallery);

    top.append(counter, close);

    const stage = document.createElement('div');
    stage.className = 'tvx-full-gallery__stage';

    const prev = document.createElement('button');
    prev.type = 'button';
    prev.className = 'tvx-full-gallery__nav tvx-full-gallery__prev';
    prev.setAttribute('aria-label', 'Previous product image');
    prev.textContent = '‹';
    prev.addEventListener('click', () => show(activeIndex - 1));

    stageImage = document.createElement('img');
    stageImage.className = 'tvx-full-gallery__image';
    stageImage.decoding = 'async';

    const next = document.createElement('button');
    next.type = 'button';
    next.className = 'tvx-full-gallery__nav tvx-full-gallery__next';
    next.setAttribute('aria-label', 'Next product image');
    next.textContent = '›';
    next.addEventListener('click', () => show(activeIndex + 1));

    stage.append(prev, stageImage, next);

    stage.addEventListener('touchstart', (event) => {
      touchStartX = event.changedTouches?.[0]?.clientX || 0;
    }, { passive: true });
    stage.addEventListener('touchend', (event) => {
      const endX = event.changedTouches?.[0]?.clientX || 0;
      const delta = endX - touchStartX;
      if (Math.abs(delta) < 48) return;
      show(activeIndex + (delta < 0 ? 1 : -1));
    }, { passive: true });

    thumbs = document.createElement('div');
    thumbs.className = 'tvx-full-gallery__thumbs';
    thumbs.setAttribute('aria-label', 'Product image thumbnails');

    modal.append(top, stage, thumbs);
    document.body.appendChild(modal);

    modal.addEventListener('click', (event) => {
      if (event.target === modal) closeGallery();
    });

    return modal;
  };

  function show(index) {
    if (!galleryItems.length || !stageImage) return;
    activeIndex = (index + galleryItems.length) % galleryItems.length;
    const item = galleryItems[activeIndex];
    stageImage.src = item.src;
    stageImage.alt = item.alt;
    counter.textContent = `${activeIndex + 1} / ${galleryItems.length}`;

    thumbs.querySelectorAll('.tvx-full-gallery__thumb').forEach((button, idx) => {
      const active = idx === activeIndex;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-current', active ? 'true' : 'false');
      if (active) button.scrollIntoView({ block: 'nearest', inline: 'center' });
    });
  }

  function openGallery(gallery, startIndex) {
    const items = collectGallery(gallery);
    if (!items.length) return;

    buildModal();
    galleryItems = items;
    thumbs.replaceChildren();

    galleryItems.forEach((item, idx) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'tvx-full-gallery__thumb';
      button.setAttribute('aria-label', `View product image ${idx + 1}`);
      const img = document.createElement('img');
      img.src = item.src;
      img.alt = '';
      img.loading = 'lazy';
      button.appendChild(img);
      button.addEventListener('click', () => show(idx));
      thumbs.appendChild(button);
    });

    lastFocus = document.activeElement;
    modal.hidden = false;
    document.documentElement.classList.add('tvx-gallery-open');
    document.body.classList.add('tvx-gallery-open');
    show(Math.min(Math.max(startIndex || 0, 0), galleryItems.length - 1));
    modal.querySelector('.tvx-full-gallery__close')?.focus({ preventScroll: true });
  }

  function closeGallery() {
    if (!modal || modal.hidden) return;
    modal.hidden = true;
    document.documentElement.classList.remove('tvx-gallery-open');
    document.body.classList.remove('tvx-gallery-open');
    stageImage?.removeAttribute('src');
    if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus({ preventScroll: true });
  }

  const firstHiddenIndex = (control, itemCount) => {
    const container = control.parentElement;
    const bullets = container
      ? Array.from(container.children).filter((item) => item.classList?.contains('swiper-pagination-bullet'))
      : [];
    const index = bullets.indexOf(control);
    return Math.min(index >= 0 ? index + 1 : 0, Math.max(itemCount - 1, 0));
  };

  const interceptPlus = (event) => {
    const control = event.target instanceof Element ? event.target.closest(PLUS_SELECTOR) : null;
    if (!control) return false;
    const gallery = control.closest('.l4pr');
    if (!gallery) return false;
    const items = collectGallery(gallery);
    if (!items.length) return false;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    openGallery(gallery, firstHiddenIndex(control, items.length));
    return true;
  };

  /* Capture on window: this runs before the old target-level +N handler. */
  window.addEventListener('click', interceptPlus, true);
  window.addEventListener('keydown', (event) => {
    const control = event.target instanceof Element ? event.target.closest(PLUS_SELECTOR) : null;
    if (control && (event.key === 'Enter' || event.key === ' ')) {
      interceptPlus(event);
      return;
    }

    if (!modal || modal.hidden) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      closeGallery();
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      show(activeIndex - 1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      show(activeIndex + 1);
    }
  }, true);
})();

/* Product detail favorite heart — uses the same tovynx:saved list as collection cards. */
(() => {
  'use strict';

  const SAVED_KEY = 'tovynx:saved';
  const STYLE_ID = 'tvx-pdp-favorite-style';
  const normalize = (value) => String(value || '').replace(/\s+/g, ' ').trim().toLowerCase();

  const readSaved = () => {
    try {
      const parsed = JSON.parse(localStorage.getItem(SAVED_KEY) || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  };

  const installStyle = () => {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      #root #main-product header.tvx-product-head-with-favorite{position:relative!important;}
      #root #main-product header.tvx-product-head-with-favorite>h1,
      #root #main-product header.tvx-product-head-with-favorite>h2,
      #root #main-product header.tvx-product-head-with-favorite>h3,
      #root #main-product header.tvx-product-head-with-favorite>h4,
      #root #main-product header.tvx-product-head-with-favorite>h5,
      #root #main-product header.tvx-product-head-with-favorite>h6{padding-right:54px!important;}
      #root #main-product .tvx-product-favorite{
        position:absolute!important;top:0!important;right:0!important;z-index:12!important;
        width:42px!important;height:42px!important;min-width:42px!important;min-height:42px!important;
        max-width:42px!important;max-height:42px!important;padding:0!important;margin:0!important;
        border:1px solid #D4DBE7!important;border-radius:999px!important;
        background:#fff!important;background-color:#fff!important;
        color:#11131A!important;-webkit-text-fill-color:#11131A!important;
        box-shadow:0 5px 15px rgba(17,19,26,.14)!important;
        font-family:Arial,sans-serif!important;font-size:25px!important;font-weight:700!important;line-height:1!important;
        display:inline-flex!important;align-items:center!important;justify-content:center!important;
        overflow:visible!important;cursor:pointer!important;text-indent:0!important;text-shadow:none!important;
        transition:transform .16s ease,background-color .16s ease,border-color .16s ease,color .16s ease,box-shadow .16s ease!important;
      }
      #root #main-product .tvx-product-favorite::before,
      #root #main-product .tvx-product-favorite::after{display:none!important;content:none!important;}
      #root #main-product .tvx-product-favorite:hover,
      #root #main-product .tvx-product-favorite:focus-visible{
        border-color:#245BDB!important;background:#F2F6FF!important;background-color:#F2F6FF!important;
        color:#245BDB!important;-webkit-text-fill-color:#245BDB!important;
        box-shadow:0 7px 18px rgba(36,91,219,.18)!important;transform:translateY(-1px) scale(1.04)!important;
      }
      #root #main-product .tvx-product-favorite.is-saved,
      #root #main-product .tvx-product-favorite[aria-pressed="true"]{
        border-color:#245BDB!important;background:#245BDB!important;background-color:#245BDB!important;
        color:#fff!important;-webkit-text-fill-color:#fff!important;box-shadow:0 7px 18px rgba(36,91,219,.28)!important;
      }
      #root #main-product .tvx-product-favorite.is-saved:hover,
      #root #main-product .tvx-product-favorite[aria-pressed="true"]:hover{
        border-color:#1744AF!important;background:#1744AF!important;background-color:#1744AF!important;
        color:#fff!important;-webkit-text-fill-color:#fff!important;
      }
      #root #main-product .tvx-product-favorite:focus-visible{outline:3px solid rgba(36,91,219,.24)!important;outline-offset:2px!important;}
      @media(max-width:760px){
        #root #main-product header.tvx-product-head-with-favorite>h1,
        #root #main-product header.tvx-product-head-with-favorite>h2,
        #root #main-product header.tvx-product-head-with-favorite>h3,
        #root #main-product header.tvx-product-head-with-favorite>h4,
        #root #main-product header.tvx-product-head-with-favorite>h5,
        #root #main-product header.tvx-product-head-with-favorite>h6{padding-right:50px!important;}
        #root #main-product .tvx-product-favorite{width:40px!important;height:40px!important;min-width:40px!important;min-height:40px!important;max-width:40px!important;max-height:40px!important;font-size:23px!important;}
      }
      @media(prefers-reduced-motion:reduce){#root #main-product .tvx-product-favorite{transition:none!important;}}
    `;
    document.head.appendChild(style);
  };

  const prepareProduct = (main) => {
    const oldWishlist = main.querySelector('.wishlist-productpage [data-product-id]');
    const heading = main.querySelector('header > h1, header > h2, header > h3, header > h4, header > h5, header > h6');
    const productId = oldWishlist?.dataset.productId || main.dataset.productId || location.pathname;
    const productUrl = main.dataset.productUrl || location.pathname;
    const productTitle = heading?.textContent?.trim() || document.querySelector('meta[property="og:title"]')?.content?.trim() || document.title;
    const priceNode = main.querySelector('.f8pr-price, .price, [data-product-price]');
    const productPrice = priceNode?.textContent?.replace(/\s+/g, ' ')?.trim() || '';
    const productImage = document.querySelector('meta[property="og:image"]')?.content || main.querySelector('.l4pr img')?.currentSrc || main.querySelector('.l4pr img')?.src || '';

    main.setAttribute('data-retention-product', '');
    main.dataset.productId = String(productId);
    main.dataset.productUrl = productUrl;
    main.dataset.productTitle = productTitle;
    if (productPrice) main.dataset.productPrice = productPrice;
    if (productImage) main.dataset.productImage = productImage;

    return { productId: String(productId), productUrl, productTitle };
  };

  const isSaved = (main) => {
    const id = String(main.dataset.productId || '');
    const url = String(main.dataset.productUrl || location.pathname);
    return readSaved().some((item) => String(item?.id || '') === id || String(item?.url || '') === url);
  };

  const syncButtons = (main) => {
    if (!main) return;
    const saved = isSaved(main);
    main.querySelectorAll('.tvx-product-favorite').forEach((button) => {
      button.classList.toggle('is-saved', saved);
      button.setAttribute('aria-pressed', String(saved));
      button.setAttribute('aria-label', saved ? 'Remove from favorites' : 'Add to favorites');
      button.setAttribute('title', saved ? 'Saved to favorites' : 'Add to favorites');
      button.textContent = saved ? '♥' : '♡';
    });
  };

  const init = () => {
    if (!document.body.classList.contains('template-product')) return;
    const main = document.querySelector('#main-product');
    if (!main) return;

    installStyle();
    const product = prepareProduct(main);
    const wantedTitle = normalize(product.productTitle);
    let headings = Array.from(main.querySelectorAll('header > h1, header > h2, header > h3, header > h4, header > h5, header > h6'));
    const matching = headings.filter((heading) => {
      const text = normalize(heading.textContent);
      return text && (text === wantedTitle || wantedTitle.startsWith(text) || text.startsWith(wantedTitle));
    });
    if (matching.length) headings = matching;
    if (!headings.length) return;

    const headers = [...new Set(headings.map((heading) => heading.parentElement).filter(Boolean))];
    headers.forEach((header) => {
      if (header.querySelector(':scope > .tvx-product-favorite')) return;
      header.classList.add('tvx-product-head-with-favorite');
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'tvx-product-favorite';
      button.setAttribute('data-tovynx-save', '');
      button.setAttribute('data-product-id', product.productId);
      button.setAttribute('aria-pressed', 'false');
      button.setAttribute('aria-label', 'Add to favorites');
      button.textContent = '♡';
      header.appendChild(button);
    });

    syncButtons(main);
  };

  init();
  document.addEventListener('shopify:section:load', () => setTimeout(init, 0));
  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('.tvx-product-favorite')) return;
    setTimeout(() => syncButtons(document.querySelector('#main-product')), 0);
  });
  window.addEventListener('storage', (event) => {
    if (event.key === SAVED_KEY) syncButtons(document.querySelector('#main-product'));
  });
})();
